<script setup lang="ts">
import { onBeforeMount, ref } from "vue";
import { useRouter } from "vue-router";
import { useHousingSiteStore } from "~/YYC_Housing/stores/HousingSiteStore";
import {
  HousingApplication,
  HousingApplicationSource,
  HousingApplicationStatus,
  LinkedWorkflowSummaryDto,
} from "~/api";
import { applicationRouteMap } from "~/router/mainRouter";
import { useMyInfoStore } from "~/stores/MyInfoStore";
import { apiClient } from "~/utils/apiClient";
import { formatDate } from "~/utils/dateUtils";
import { showError } from "~/utils/handleAPIError";
import { splitByCaps } from "~/utils/miscUtils";
import { housingApplicationStatusDisplayNamesPublicSite } from "../../utils/enumDisplayNames";
import { ApplicationWizardStep } from "../types/plusTypes";
import ApplicationSection from "./ApplicationSection.vue";

const housingSiteStore = useHousingSiteStore();
const myInfoStore = useMyInfoStore();

const router = useRouter();
const loading = ref(true);
const applications = ref<HousingApplication[]>([]);

const createHousingApplication = async () => {
  const source = HousingApplicationSource.SENIOR_APPLICATION;

  await apiClient.housingApplication
    .createHousingApplication(
      myInfoStore.currentSysOrgGuid,
      source,
      true, // myInfoStore.isBulkAgent ? true : true,
    )
    .then((application) => {
      applications.value.unshift(application);
      router.push({
        name: applicationRouteMap.Intake.children.Application.children
          .ApplicationWizard.name,
        params: {
          step: ApplicationWizardStep.ReviewAndSubmit,
          applicationGuid: application.guid,
        },
      });
    })
    .catch(showError);
};

const getApplications = async () => {
  const trackedStatuses = new Set([
    HousingApplicationStatus.DRAFT,
    HousingApplicationStatus.IN_REVIEW,
    HousingApplicationStatus.NEW,
  ]);
  await apiClient.housingApplication
    .getMyHousingApplications(myInfoStore.currentSysOrgGuid) // just ones I've created
    .then((applicationList) => {
      if (myInfoStore.isBulkAgent) {
        applications.value = applicationList.filter(
          (application) =>
            application.isTracked || trackedStatuses.has(application.status),
        );
      } else {
        applications.value = applicationList;
      }
      sortApplicationsByLastActivity(applications.value);
    })
    .catch(showError);
};

const getWorkflowsByDefinitionSorted = (
  application: HousingApplication,
  ...workflowDefinitionGuids: string[]
): LinkedWorkflowSummaryDto[] => {
  const guidSet = new Set(workflowDefinitionGuids.map((g) => g.toLowerCase()));
  return (
    application.workflows
      ?.filter(
        (w) =>
          guidSet.has(w.workflowDefinitionGuid?.toLowerCase() ?? "") &&
          // todo: hacky way for it to not show up but we should not be fetching it at all
          w.workflowStatus !== "OfferPending" &&
          w.workflowStatus !== "OfferInProgress",
      )
      .sort(
        (a, b) =>
          new Date(b.workflowUpdatedAt).getTime() -
          new Date(a.workflowUpdatedAt).getTime(),
      ) ?? []
  );
};

const sortApplicationsByLastActivity = (
  applicationsLocal: HousingApplication[],
): HousingApplication[] => {
  return applicationsLocal.sort((a, b) => {
    const dateA = Math.max(
      new Date(a.dateLastActivity || 0).getTime(),
      new Date(a.dateCreated || 0).getTime(),
    );
    const dateB = Math.max(
      new Date(b.dateLastActivity || 0).getTime(),
      new Date(b.dateCreated || 0).getTime(),
    );
    return dateB - dateA;
  });
};

const handleCreateUpdateApplicationRequest = async (
  application: HousingApplication,
) => {
  try {
    const existing = application.workflows?.find(
      (w) =>
        w.workflowDefinitionName === "My Information Update" &&
        !w.isWorkflowComplete,
    );
    const workflowGuid =
      existing?.workflowGuid ??
      (await apiClient.housingApplication.createWorkflowForApplication(
        application.guid,
        { workflowDefinitionName: "My Information Update" },
      ));
    if (!existing) await getApplications();
    router.push({
      name: "intakeWorkflowResponse",
      params: { workflowGuid },
    });
  } catch (e) {
    showError(e);
  }
};

const handleViewWorkflow = (workflowGuid: string): void => {
  router.push({
    name: "intakeWorkflowResponse",
    params: { workflowGuid },
  });
};

onBeforeMount(async () => {
  loading.value = true;
  await myInfoStore.load();
  await Promise.all([getApplications(), housingSiteStore.load()]);
  loading.value = false;
});
</script>

<template>
  <div v-loading="loading" class="ApplicationHome">
    <div v-if="myInfoStore.isAgent || myInfoStore.isBulkAgent" class="agent">
      <p>
        You are registered as a
        {{ myInfoStore.isAgent ? "agent" : "bulk agent" }}. You can create and
        manage multiple applications.
      </p>
      <el-button
        v-if="
          !loading &&
          (myInfoStore.isAgent ||
            myInfoStore.isBulkAgent ||
            applications.length == 0)
        "
        class="UpdateApplicationRequestButton"
        type="info"
        @click="createHousingApplication"
      >
        Create
        {{ applications.length > 0 ? "Another" : "" }}
        Application
      </el-button>
    </div>
    <p class="main">
      You will see communications from YYC Seniors Housing here. If you need to
      update your application or communicate with YYC Seniors Housing, click the
      <em>Update My Information</em> button below.
    </p>
    <div
      v-for="application in applications"
      :key="application.guid"
      class="SingleApplicationContainer"
    >
      <div class="SingleApplicationContainerHeading">
        <h2
          v-if="
            myInfoStore.isAgent ||
            myInfoStore.isBulkAgent ||
            applications.length > 1
          "
        >
          {{ application.applicantName }}
          <el-tag>Created: {{ formatDate(application.dateCreated, 1) }}</el-tag>
        </h2>
      </div>

      <div class="SingleApplicationContainerInner">
        <!-- THE APPLICATION -->
        <ApplicationSection>
          <template #header>
            <div class="ApplicationFormHeader">
              <h3 :title="application.applicantName">My Housing Application</h3>
              <span>
                <span v-if="application.dateSubmitted">
                  Submitted {{ formatDate(application.dateSubmitted, 0) }}
                </span>
                <span v-if="applications.length === 1">
                  {{ application.applicantName }}
                </span>
              </span>
            </div>
          </template>
          <template #status>
            {{
              housingApplicationStatusDisplayNamesPublicSite[application.status]
            }}
          </template>
          <template #button>
            <el-button
              type="primary"
              class="SingleApplicationButton"
              @click="
                router.push({
                  name: applicationRouteMap.Intake.children.Application.children
                    .ApplicationWizard.name,
                  params: {
                    step: ApplicationWizardStep.ReviewAndSubmit,
                    applicationGuid: application.guid,
                  },
                })
              "
            >
              View
            </el-button>
          </template>
        </ApplicationSection>
        <!-- END -->

        <!-- OFFERS -->
        <ApplicationSection
          v-for="(offerForm, i) in getWorkflowsByDefinitionSorted(
            application,
            '454056A6-B722-F111-9A49-6045BD5B8A82',
            'B039E65F-C044-F111-8EF3-002248AEBDFE',
          )"
          :key="offerForm.workflowGuid"
        >
          <template #header>
            <div class="ApplicationFormHeader">
              <h3>
                <template
                  v-if="
                    getWorkflowsByDefinitionSorted(
                      application,
                      '454056A6-B722-F111-9A49-6045BD5B8A82',
                      'B039E65F-C044-F111-8EF3-002248AEBDFE',
                    ).length === 1
                  "
                >
                  {{ offerForm.workflowName }}
                </template>

                <template v-else>
                  {{ offerForm.workflowName }}
                  {{
                    getWorkflowsByDefinitionSorted(
                      application,
                      "454056A6-B722-F111-9A49-6045BD5B8A82",
                      "B039E65F-C044-F111-8EF3-002248AEBDFE",
                    ).length - i
                  }}
                </template>
              </h3>
              <span> {{ formatDate(offerForm.linkedAt) }} </span>
            </div>
          </template>
          <template #status>
            {{
              offerForm.workflowStatus == "OfferInProgressVisibleToApplicant"
                ? "Offer In Progress"
                : splitByCaps(offerForm.workflowStatus)
            }}
          </template>
          <template #button>
            <el-button
              type="primary"
              class="SingleApplicationButton"
              @click="handleViewWorkflow(offerForm.workflowGuid)"
            >
              View
            </el-button>
          </template>
        </ApplicationSection>
        <!-- END -->

        <!-- UPDATE REQUESTS -->
        <ApplicationSection
          v-for="(updateRequest, i) in getWorkflowsByDefinitionSorted(
            application,
            '625f661e-b722-f111-9a49-6045bd5b8a82',
          )"
          :key="updateRequest.workflowGuid"
        >
          <template #header>
            <div class="ApplicationFormHeader">
              <h3>
                <template
                  v-if="
                    getWorkflowsByDefinitionSorted(
                      application,
                      '625f661e-b722-f111-9a49-6045bd5b8a82',
                    ).length === 1
                  "
                >
                  {{ updateRequest.workflowName }}
                </template>

                <template v-else>
                  {{ updateRequest.workflowName }}
                  {{
                    getWorkflowsByDefinitionSorted(
                      application,
                      "625f661e-b722-f111-9a49-6045bd5b8a82",
                    ).length - i
                  }}
                </template>
              </h3>
              <span>
                Last Edited {{ formatDate(updateRequest.workflowUpdatedAt) }}
              </span>
            </div>
          </template>
          <template #status>
            {{ splitByCaps(updateRequest.workflowStatus) }}
          </template>
          <template #button>
            <el-button
              type="primary"
              class="SingleApplicationButton"
              @click="handleViewWorkflow(updateRequest.workflowGuid)"
            >
              View
            </el-button>
          </template>
        </ApplicationSection>
        <!-- END -->

        <!-- HMB UPDATE REQUESTS -->
        <ApplicationSection
          v-for="(updateRequest, i) in getWorkflowsByDefinitionSorted(
            application,
            '4E6FF302-B722-F111-9A49-6045BD5B8A82',
          )"
          :key="updateRequest.workflowGuid"
        >
          <template #header>
            <div class="ApplicationFormHeader">
              <h3>
                <template
                  v-if="
                    getWorkflowsByDefinitionSorted(
                      application,
                      '4E6FF302-B722-F111-9A49-6045BD5B8A82',
                    ).length === 1
                  "
                >
                  {{ updateRequest.workflowName }}
                </template>

                <template v-else>
                  {{ updateRequest.workflowName }}
                  {{
                    getWorkflowsByDefinitionSorted(
                      application,
                      "4E6FF302-B722-F111-9A49-6045BD5B8A82",
                    ).length - i
                  }}
                </template>
              </h3>
              <span>
                Last Edited {{ formatDate(updateRequest.workflowUpdatedAt) }}
              </span>
            </div>
          </template>
          <template #status>
            {{ splitByCaps(updateRequest.workflowStatus) }}
          </template>
          <template #button>
            <el-button
              type="primary"
              class="SingleApplicationButton"
              @click="handleViewWorkflow(updateRequest.workflowGuid)"
            >
              View
            </el-button>
          </template>
        </ApplicationSection>
        <!-- END -->
        <el-button
          class="UpdateApplicationRequestButton"
          type="info"
          @click="() => handleCreateUpdateApplicationRequest(application)"
        >
          Update My Information
        </el-button>
      </div>
    </div>
    <div v-if="applications.length === 0 && !loading" class="NoApplications">
      <h2>You have no applications yet.</h2>
      <p>
        Click the
        <el-button type="info" @click="createHousingApplication">
          Create Application
        </el-button>
        button to get started.
      </p>
    </div>
  </div>
</template>

<style lang="less">
.ApplicationHome {
  display: flex;
  flex-direction: column;
  margin: 0 32px 2em;
  place-content: center;

  .ApplicationFormHeader {
    display: flex;
    flex-direction: column;
    h3 {
      font-size: 0.9em;
      margin: 0;
    }
    > span {
      font-size: 0.6em;
      color: var(--el-color-primary-light-7);
    }
  }
  .CreateApplicationButton {
    width: min-content;
  }

  .main {
    margin: 1em auto;
    width: 100%;
    max-width: 1160px;
  }

  .agent {
    margin: 1em auto;
    width: 100%;
    max-width: 1200px;
    background-color: var(--el-menu-item-hover-fill);
    padding: 0 1em 1em 1em;
    border-radius: 8px;
    box-shadow: 0px 0px 6px 4px hsl(0deg 0% 0% / 15%);
  }

  .SingleApplicationContainer {
    margin: 1em auto 3em;
    width: 100%;

    max-width: 1200px;
    .SingleApplicationContainerHeading {
      h2 {
        margin: 0;
        margin-bottom: 16px;
      }
    }
    .SingleApplicationContainerInner {
      display: flex;
      flex-direction: column;
      gap: 8px;
      margin-left: 16px;
      margin-bottom: 32px;
    }
  }

  .UpdateApplicationRequestButton {
    margin-top: 4px;
    width: min-content;
    font-size: 0.8em;
    border: 1px solid var(--el-color-primary-light-5);
  }

  @media screen and (max-width: 600px) {
    font-size: 0.7em;
  }
  @media screen and (max-width: 450px) {
    .ApplicationSectionComponent {
      font-size: 0.8em;
      width: 25em;
      margin-left: -4em;
    }
  }
}
</style>
